import {DestroyRef, Injectable, inject, signal} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

import {DiscussionVisibilityService} from "./discussion-visibility.service";
import {RecommendationDiscussionsService} from "./recommendation-discussions.service";
import {
  DiscussionTypingLease,
  DiscussionTypingUpdate,
} from "../interfaces/discussion-typing.interface";
import {
  DISCUSSION_TYPING_HEARTBEAT_MS,
  DISCUSSION_TYPING_IDLE_MS,
  DISCUSSION_TYPING_MAX_SOURCES,
  DISCUSSION_TYPING_TTL_MS,
} from "../constants/discussion-typing.constants";

@Injectable()
export class DiscussionTypingState {
  readonly visible = signal(false);
  private readonly api = inject(RecommendationDiscussionsService);
  private readonly visibility = inject(DiscussionVisibilityService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sources = new Map<string, DiscussionTypingLease>();
  private threadId = "";
  private otherId = "";
  private allowed = false;
  private foreground = document.visibilityState === "visible";
  private active = false;
  private changedAt = 0;
  private sentAt = 0;
  private localTimer?: ReturnType<typeof setTimeout>;
  private remoteTimer?: ReturnType<typeof setTimeout>;

  initialize(threadId: string): void {
    this.threadId = threadId;
    this.api.typing$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((update) => this.receive(update));
    this.api.socket.ready$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ready) => {
        if (!ready) this.clear();
      });
    this.visibility.active$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((active) => this.background(active));
    window.addEventListener("offline", this.offline);
    this.destroyRef.onDestroy(() => {
      this.clear();

      window.removeEventListener("offline", this.offline);
    });
  }

  configure(otherId: string, allowed: boolean): void {
    this.otherId = otherId;
    this.allowed = allowed;
    if (!allowed) this.clear();
  }

  input(nonempty: boolean): void {
    if (
      !nonempty ||
      !this.allowed ||
      !this.foreground ||
      document.visibilityState !== "visible"
    ) {
      this.stop();
      return;
    }
    const now = performance.now();
    this.changedAt = now;
    if (!this.active || now - this.sentAt >= DISCUSSION_TYPING_HEARTBEAT_MS)
      this.sendActive(now);
    this.schedule();
  }

  stop(): void {
    clearTimeout(this.localTimer);
    this.localTimer = undefined;
    if (this.active)
      this.api.socket.emitTyping({threadId: this.threadId, typing: false});
    this.active = false;
  }

  private readonly offline = () => this.clear();

  private background(active: boolean): void {
    this.foreground = active;
    if (!active) this.clear();
  }

  private clear(): void {
    this.stop();
    clearTimeout(this.remoteTimer);
    this.remoteTimer = undefined;
    this.sources.clear();
    this.visible.set(false);
  }

  private sendActive(now: number): void {
    this.active = this.api.socket.emitTyping({
      threadId: this.threadId,
      typing: true,
    });
    this.sentAt = now;
  }

  private schedule(): void {
    clearTimeout(this.localTimer);
    if (!this.active) return;
    const now = performance.now();
    const idle = this.changedAt + DISCUSSION_TYPING_IDLE_MS - now;
    const renewal = this.sentAt + DISCUSSION_TYPING_HEARTBEAT_MS - now;
    this.localTimer = setTimeout(
      () => {
        const current = performance.now();
        if (current - this.changedAt >= DISCUSSION_TYPING_IDLE_MS) {
          this.stop();
          return;
        }
        this.sendActive(current);
        this.schedule();
      },
      Math.max(1, Math.min(idle, renewal))
    );
  }

  private receive(update: DiscussionTypingUpdate): void {
    if (
      !this.allowed ||
      !this.foreground ||
      document.visibilityState !== "visible" ||
      update.threadId !== this.threadId ||
      update.authorId !== this.otherId ||
      typeof update.sourceId !== "string" ||
      update.sourceId.length > 100 ||
      !Number.isSafeInteger(update.revision) ||
      update.revision < 1 ||
      typeof update.typing !== "boolean" ||
      !Number.isFinite(update.expiresInMs) ||
      update.expiresInMs < 0 ||
      update.expiresInMs > DISCUSSION_TYPING_TTL_MS
    )
      return;
    const known = this.sources.get(update.sourceId);
    if (known && known.revision >= update.revision) return;
    if (!known && this.sources.size >= DISCUSSION_TYPING_MAX_SOURCES) return;
    this.sources.set(update.sourceId, {
      revision: update.revision,
      expiresAt: update.typing ? performance.now() + update.expiresInMs : 0,
      forgetAt: performance.now() + DISCUSSION_TYPING_TTL_MS * 2,
    });
    this.expire();
  }

  private expire(): void {
    clearTimeout(this.remoteTimer);
    const now = performance.now();
    for (const [id, source] of this.sources)
      if (source.forgetAt <= now) this.sources.delete(id);
    const active = [...this.sources.values()].filter(
      (source) => source.expiresAt > now
    );
    this.visible.set(active.length > 0);
    const deadlines = [...this.sources.values()].map((source) =>
      source.expiresAt > now ? source.expiresAt : source.forgetAt
    );
    if (deadlines.length)
      this.remoteTimer = setTimeout(
        () => this.expire(),
        Math.max(1, Math.min(...deadlines) - now)
      );
  }
}
